/**
 * Support Tab Component (Admin)
 *
 * Admin view for managing all user support tickets.
 * Features: Status filtering, ticket list, detail view, admin replies, status/priority updates.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adminGetTickets,
  adminUpdateTicket,
  adminAddTicketMessage,
  getTicketDetail,
  type SupportTicket,
  type TicketMessage,
} from '@/services/api';

// Status options with colors - labels are i18n keys
const STATUS_OPTIONS = [
  { value: 'all', labelKey: 'supportTab.statuses.all', color: 'bg-gray-100 text-gray-700', border: 'border-gray-300' },
  { value: 'open', labelKey: 'supportTab.statuses.open', color: 'bg-blue-100 text-blue-700', border: 'border-blue-300' },
  { value: 'in_progress', labelKey: 'supportTab.statuses.inProgress', color: 'bg-yellow-100 text-yellow-700', border: 'border-yellow-300' },
  { value: 'waiting', labelKey: 'supportTab.statuses.waiting', color: 'bg-purple-100 text-purple-700', border: 'border-purple-300' },
  { value: 'resolved', labelKey: 'supportTab.statuses.resolved', color: 'bg-green-100 text-green-700', border: 'border-green-300' },
  { value: 'closed', labelKey: 'supportTab.statuses.closed', color: 'bg-gray-100 text-gray-700', border: 'border-gray-300' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', labelKey: 'supportTab.priorities.low', color: 'bg-gray-100 text-gray-700' },
  { value: 'normal', labelKey: 'supportTab.priorities.normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', labelKey: 'supportTab.priorities.high', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', labelKey: 'supportTab.priorities.urgent', color: 'bg-red-100 text-red-700' },
];

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  billing: 'supportTab.categories.billing',
  technical: 'supportTab.categories.technical',
  account: 'supportTab.categories.account',
  feature_request: 'supportTab.categories.featureRequest',
  other: 'supportTab.categories.other',
};

const getStatusColor = (status: string): string => {
  const option = STATUS_OPTIONS.find(s => s.value === status);
  return option?.color || 'bg-gray-100 text-gray-700';
};

const getPriorityColor = (priority: string): string => {
  const option = PRIORITY_OPTIONS.find(p => p.value === priority);
  return option?.color || 'bg-gray-100 text-gray-700';
};

// formatDate is defined inside component to access t()

export default function SupportTab() {
  const { t } = useTranslation('admin');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return t('supportTab.time.justNow');
    if (hours < 24) return t('supportTab.time.hoursAgo', { hours });
    if (days < 7) return t('supportTab.time.daysAgo', { days });
    return date.toLocaleDateString();
  };

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');

  // Admin actions
  const [newReply, setNewReply] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Load tickets
  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const data = await adminGetTickets(status);
      setTickets(data.tickets);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tickets';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Load ticket detail
  const viewTicket = async (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setAdminNotes(ticket.admin_notes || '');
    setIsLoadingDetail(true);
    try {
      const data = await getTicketDetail(ticket.id);
      setTicketMessages(data.messages);
    } catch (err: unknown) {
      console.error('Failed to load ticket details:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Send admin reply
  const sendReply = async () => {
    if (!selectedTicket || !newReply.trim()) return;
    setIsSendingReply(true);
    try {
      await adminAddTicketMessage(selectedTicket.id, newReply);
      const data = await getTicketDetail(selectedTicket.id);
      setTicketMessages(data.messages);
      setNewReply('');
      showSuccess(t('supportTab.replySent'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reply';
      setError(errorMessage);
    } finally {
      setIsSendingReply(false);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (newStatus: string) => {
    if (!selectedTicket) return;
    setIsUpdating(true);
    try {
      const updated = await adminUpdateTicket(selectedTicket.id, { status: newStatus });
      setSelectedTicket(updated);
      setTickets(prev => prev.map(tk => tk.id === updated.id ? updated : tk));
      showSuccess(t('supportTab.statusUpdated'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Update ticket priority
  const updateTicketPriority = async (newPriority: string) => {
    if (!selectedTicket) return;
    setIsUpdating(true);
    try {
      const updated = await adminUpdateTicket(selectedTicket.id, { priority: newPriority });
      setSelectedTicket(updated);
      setTickets(prev => prev.map(tk => tk.id === updated.id ? updated : tk));
      showSuccess(t('supportTab.priorityUpdated'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update priority';
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Save admin notes
  const saveAdminNotes = async () => {
    if (!selectedTicket) return;
    setIsSavingNotes(true);
    try {
      const updated = await adminUpdateTicket(selectedTicket.id, { admin_notes: adminNotes });
      setSelectedTicket(updated);
      setTickets(prev => prev.map(tk => tk.id === updated.id ? updated : tk));
      showSuccess(t('supportTab.notesSaved'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save notes';
      setError(errorMessage);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Count tickets by status
  const statusCounts = tickets.reduce((acc, ticket) => {
    acc[ticket.status] = (acc[ticket.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        <span className="ml-3 text-gray-600">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Status Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('supportTab.title')}</h2>
              <p className="text-sm text-gray-500">{tickets.length} {t('supportTab.totalTickets')}</p>
            </div>
          </div>
          <button
            onClick={loadTickets}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
          >
            {t('supportTab.refresh')}
          </button>
        </div>

        {/* Status Filter Pills */}
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((status) => {
            const count = status.value === 'all'
              ? tickets.length
              : (statusCounts[status.value] || 0);
            const isActive = statusFilter === status.value;

            return (
              <button
                key={status.value}
                onClick={() => setStatusFilter(status.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                  isActive
                    ? `${status.color} ${status.border}`
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {t(status.labelKey)}
                {count > 0 && (
                  <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                    isActive ? 'bg-white/50' : 'bg-gray-100'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Messages */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
            {successMessage}
          </div>
        )}
      </div>

      {/* Main Content: List + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{t('supportTab.tickets')}</h3>
            </div>
            {tickets.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                {t('supportTab.noTicketsFound')}
              </div>
            ) : (
              <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                {tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => viewTicket(ticket)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedTicket?.id === ticket.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority.toUpperCase()}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900 truncate text-sm">{ticket.subject}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span>{ticket.username}</span>
                      <span>•</span>
                      <span>{CATEGORY_LABEL_KEYS[ticket.category] ? t(CATEGORY_LABEL_KEYS[ticket.category]) : ticket.category}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(ticket.created_at)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Ticket Detail */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Ticket Header */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{selectedTicket.subject}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('supportTab.ticketNumber', { id: selectedTicket.id })} • {selectedTicket.username} • {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Status & Priority Controls */}
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{t('supportTab.status')}</span>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => updateTicketStatus(e.target.value)}
                      disabled={isUpdating}
                      className={`px-3 py-1 text-sm rounded-lg border ${getStatusColor(selectedTicket.status)} border-gray-300 focus:ring-2 focus:ring-orange-500`}
                    >
                      {STATUS_OPTIONS.filter(s => s.value !== 'all').map((status) => (
                        <option key={status.value} value={status.value}>
                          {t(status.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{t('supportTab.priority')}</span>
                    <select
                      value={selectedTicket.priority}
                      onChange={(e) => updateTicketPriority(e.target.value)}
                      disabled={isUpdating}
                      className={`px-3 py-1 text-sm rounded-lg border ${getPriorityColor(selectedTicket.priority)} border-gray-300 focus:ring-2 focus:ring-orange-500`}
                    >
                      {PRIORITY_OPTIONS.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {t(priority.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-sm text-gray-500">
                    {CATEGORY_LABEL_KEYS[selectedTicket.category] ? t(CATEGORY_LABEL_KEYS[selectedTicket.category]) : selectedTicket.category}
                  </span>
                </div>
              </div>

              {/* Original Description */}
              <div className="p-6 border-b border-gray-200 bg-blue-50">
                <p className="text-xs font-medium text-blue-600 mb-2">{t('supportTab.originalRequest')}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Conversation Thread */}
              <div className="p-6 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-4">{t('supportTab.conversation')}</h4>
                {isLoadingDetail ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
                  </div>
                ) : ticketMessages.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">{t('supportTab.noRepliesYet')}</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {ticketMessages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.is_admin ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[80%] rounded-lg p-3 ${
                          msg.is_admin ? 'bg-orange-100 text-gray-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          <p className="text-xs mb-1 font-medium">
                            {msg.is_admin ? t('supportTab.supportTeam') : msg.username} • {new Date(msg.created_at).toLocaleString()}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <p className="text-xs font-medium text-gray-600 mb-2">{t('supportTab.replyAsAdmin')}</p>
                  <div className="flex gap-2">
                    <textarea
                      value={newReply}
                      onChange={(e) => setNewReply(e.target.value)}
                      placeholder={t('supportTab.typeYourReply')}
                      rows={2}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
                    />
                    <button
                      onClick={sendReply}
                      disabled={!newReply.trim() || isSendingReply}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
                    >
                      {isSendingReply ? t('supportTab.sending') : t('supportTab.send')}
                    </button>
                  </div>
                </div>
              )}

              {/* Admin Notes (Internal) */}
              <div className="p-4 bg-yellow-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-yellow-700">{t('supportTab.internalAdminNotes')}</p>
                  <span className="text-xs text-yellow-600">{t('supportTab.notVisibleToUser')}</span>
                </div>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={t('supportTab.addInternalNotesPlaceholder')}
                  rows={2}
                  className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none text-sm bg-white"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={saveAdminNotes}
                    disabled={isSavingNotes || adminNotes === (selectedTicket.admin_notes || '')}
                    className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSavingNotes ? t('supportTab.saving') : t('supportTab.saveNotes')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <p className="text-lg font-medium">{t('supportTab.selectTicket')}</p>
              <p className="text-sm mt-1">{t('supportTab.clickToManage')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
